import { create } from 'zustand';

/**
 * Auth store.
 *
 * Access- и refresh-токены хранятся в localStorage: сессия должна переживать
 * перезагрузку страницы, а лендинг — сразу узнавать вошедшего пользователя.
 * Плата за это — доступность токенов из JS при XSS; httpOnly cookie была бы
 * безопаснее, но требует доработки gateway (см. plan § Open Q2).
 *
 * Auth flow (соответствует iOS AuthService.swift):
 *   1) GetSMSCode(phone) → user enters code
 *   2) Authenticate(code, phone) → { accessToken, refreshToken, status }
 *   3) status === 'NEW' → registration wizard
 *      status === 'FULL_ACCESS' → /app
 *      status === 'BLOCKED' → blocked screen
 */

export type AuthStatus = 'NEW' | 'FULL_ACCESS' | 'BLOCKED' | 'DELETED' | string;

interface PersistedTokens {
  refreshToken: string | null;
  accessToken: string | null;
  status: AuthStatus | null;
}

const PERSIST_KEY = 'aura_refresh_token';

const EMPTY_TOKENS: PersistedTokens = {
  refreshToken: null,
  accessToken: null,
  status: null,
};

function loadPersisted(): PersistedTokens {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return EMPTY_TOKENS;
    const parsed = JSON.parse(raw) as Partial<PersistedTokens>;
    return {
      refreshToken: parsed.refreshToken ?? null,
      accessToken: parsed.accessToken ?? null,
      status: parsed.status ?? null,
    };
  } catch {
    return EMPTY_TOKENS;
  }
}

function savePersisted(tokens: PersistedTokens): void {
  try {
    localStorage.setItem(PERSIST_KEY, JSON.stringify(tokens));
  } catch {
    /* приватный режим / квота */
  }
}

/** Apollo authLink читает токен из window — держим его в одном месте. */
function setAuthHeaderToken(token: string | null): void {
  (window as unknown as { __authToken?: string }).__authToken = token ?? undefined;
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(PERSIST_KEY);
  } catch {
    /* ignore */
  }
}

export interface AuthStore {
  /** Access-токен (persisted в localStorage). */
  accessToken: string | null;
  /** Refresh-токен (persisted в localStorage). */
  refreshToken: string | null;
  /** ID пользователя (после успешного входа). */
  userId: string | null;
  /** Статус после authenticate (NEW | FULL_ACCESS | BLOCKED | DELETED). */
  status: AuthStatus | null;
  /** Удобный флаг для ProtectedRoute (только FULL_ACCESS). */
  isAuthenticated: boolean;
  /**
   * Токены есть, но регистрация не завершена (status === 'NEW').
   * Запросы уже авторизованы, поэтому визард регистрации работает,
   * а остальные разделы остаются закрытыми.
   */
  needsRegistration: boolean;
  /** Регистрация завершена — сервер вернул FULL_ACCESS. */
  completeRegistration: () => void;

  /** Логин: сохраняет токены, ставит isAuthenticated = true (если FULL_ACCESS). */
  signIn: (params: {
    accessToken: string;
    refreshToken: string;
    status: AuthStatus;
    userId?: string;
  }) => void;
  /** Обновление access-токена (silent refresh). */
  refreshAccessToken: (accessToken: string, refreshToken?: string) => void;
  /** Сохранить ID текущего пользователя (после GetFullProfile). */
  setUserId: (userId: string) => void;
  /** Логаут: очищает всё. */
  signOut: () => void;
  /** Гидратация при старте приложения (если refresh есть — попытаемся refresh). */
  hasRefreshToken: () => boolean;
}

/** Стартовое состояние стора из localStorage. */
const restored = loadPersisted();

// Apollo authLink читает токен отсюда — подставляем ещё до монтирования React.
setAuthHeaderToken(restored.accessToken);

export const useAuthStore = create<AuthStore>((set, get) => ({
  accessToken: restored.accessToken,
  refreshToken: restored.refreshToken,
  userId: null,
  status: restored.status,
  isAuthenticated: Boolean(restored.accessToken) && restored.status === 'FULL_ACCESS',
  needsRegistration: Boolean(restored.accessToken) && restored.status === 'NEW',

  signIn: ({ accessToken, refreshToken, status, userId }) => {
    savePersisted({ refreshToken, accessToken, status });
    setAuthHeaderToken(accessToken);
    set({
      accessToken,
      refreshToken,
      status,
      userId: userId ?? null,
      isAuthenticated: status === 'FULL_ACCESS',
      needsRegistration: status === 'NEW',
    });
  },

  completeRegistration: () => {
    const { accessToken, refreshToken } = get();
    savePersisted({ accessToken, refreshToken, status: 'FULL_ACCESS' });
    set({ status: 'FULL_ACCESS', isAuthenticated: true, needsRegistration: false });
  },

  refreshAccessToken: (accessToken, refreshToken) => {
    setAuthHeaderToken(accessToken);
    const state = get();
    savePersisted({
      accessToken,
      refreshToken: refreshToken ?? state.refreshToken,
      // Обновление токена само по себе не меняет статус аккаунта.
      status: state.status ?? 'FULL_ACCESS',
    });
    set({
      accessToken,
      refreshToken: refreshToken ?? state.refreshToken,
      isAuthenticated: (state.status ?? 'FULL_ACCESS') === 'FULL_ACCESS',
    });
  },

  setUserId: (userId) => set({ userId }),

  signOut: () => {
    setAuthHeaderToken(null);
    clearPersisted();
    set({
      accessToken: null,
      refreshToken: null,
      userId: null,
      status: null,
      isAuthenticated: false,
      needsRegistration: false,
    });
  },

  hasRefreshToken: () => Boolean(get().refreshToken),
}));

/**
 * Возвращаем persisted refresh-токен (для Apollo errorLink silent refresh).
 * Доступен до того, как React загрузит auth.store.
 */
export function getStoredRefreshToken(): string | null {
  return loadPersisted().refreshToken;
}

/** Persisted access-токен — им авторизуется первый же запрос после перезагрузки. */
export function getStoredAccessToken(): string | null {
  return loadPersisted().accessToken;
}
